/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { DeviceConfigurationService } from "@tivocorp/data-domain-lib";

import { Log } from "../../model/logging";
import { BrandingInteractor } from "../../model/interactor";
import {
  PRIMARY_BRANDING_LOGO_HEIGHT_1080P,
  PRIMARY_BRANDING_LOGO_WIDTH_1080P,
  DEFAULT_EPG_LOOK_BACK_HOURS,
  DEFAULT_EAS_DISPLAY_MODE
} from "../../config";
import { StorageAdapter } from "../../model/storage";
import { STORAGE_KEY_PARTNER_LOGO_URL } from "../../model/storage/storageConfig";
import { Branding } from "../../model/branding/Branding";
import { BrandingUiInfoKeys } from "../../model/entities/BrandingUiInfoKeys";
import type { UnityRootState } from "../UnityAppStore";
import type { BrandingState } from "./branding.types";

const TAG: string = "brandingSlice";

const INITIAL_STATE: BrandingState = {
  isLoading: false,
  isLoaded: false,
  error: null,
  partnerLogoUrl: "",
  epgLookBackHours: DEFAULT_EPG_LOOK_BACK_HOURS,
  easDisplayMode: DEFAULT_EAS_DISPLAY_MODE
};

/**
 * Async thunk that fetches the post-sign-in branding UI bundle.
 *
 * Requires an access token (post-sign-in). The interactor resolves
 * msoPartnerId, msoServiceId, and slsDomain internally.  The branding
 * response is cached inside MsoBrandingUiService for later lookup
 * via getBrandingUiInfoForKey.
 */
export const fetchBrandingUiBundle = createAsyncThunk<
  { partnerLogoUrl: string; epgLookBackHours: number; easDisplayMode: string },
  void,
  { rejectValue: string }
>("branding/fetchBrandingUiBundle", async (_: void, { rejectWithValue }) => {
  try {
    const interactor: BrandingInteractor = new BrandingInteractor();

    Log.d(TAG, "fetchBrandingUiBundle() called");
    // Session-scoped: this runs inside postSigninStartupSequence, which
    // outlives the post sign-in redirect chain, so it must not be aborted
    // by NetworkGuard.cancelAllForNavigation().
    await interactor.fetchBrandingUiBundle({
      bypassNavigationCancellation: true
    });
    Log.d(TAG, "fetchBrandingUiBundle() — complete");

    const logoUrl: string =
      DeviceConfigurationService.getPartnerBrandingImageUrl(
        PRIMARY_BRANDING_LOGO_WIDTH_1080P,
        PRIMARY_BRANDING_LOGO_HEIGHT_1080P
      );

    try {
      const storage: StorageAdapter = StorageAdapter.getInstance();
      await storage.set(
        STORAGE_KEY_PARTNER_LOGO_URL,
        logoUrl,
        Number.POSITIVE_INFINITY
      );
      Log.d(
        TAG,
        "fetchBrandingUiBundle() — partnerLogoUrl persisted to storage"
      );
    } catch (storageError: unknown) {
      Log.w(
        TAG,
        "fetchBrandingUiBundle() — failed to persist partnerLogoUrl",
        storageError
      );
    }

    const rawHours: unknown = Branding.getBrandingUiInfoForKey(
      BrandingUiInfoKeys.EPG_LOOK_BACK_HOURS
    );
    const epgLookBackHours: number =
      typeof rawHours === "number" && rawHours > 0
        ? rawHours
        : DEFAULT_EPG_LOOK_BACK_HOURS;

    // easDisplayMode is nested under easConfiguration in the branding bundle.
    const easConfiguration: unknown = Branding.getBrandingUiInfoForKey(
      BrandingUiInfoKeys.EAS_CONFIGURATION
    );
    const rawEasDisplayMode: unknown =
      typeof easConfiguration === "object" && easConfiguration !== null
        ? (easConfiguration as Record<string, unknown>)[
            BrandingUiInfoKeys.EAS_DISPLAY_MODE
          ]
        : undefined;
    const easDisplayMode: string =
      typeof rawEasDisplayMode === "string" && rawEasDisplayMode.length > 0
        ? rawEasDisplayMode
        : DEFAULT_EAS_DISPLAY_MODE;

    return { partnerLogoUrl: logoUrl, epgLookBackHours, easDisplayMode };
  } catch (error: unknown) {
    Log.e(TAG, "fetchBrandingUiBundle()", error);
    const message: string =
      error instanceof Error
        ? error.message
        : "Failed to fetch branding UI bundle";
    return rejectWithValue(message);
  }
});

const brandingSlice = createSlice({
  name: "branding",
  initialState: INITIAL_STATE,
  reducers: {
    setPartnerLogoUrl(state, action: { payload: string }) {
      if (action.payload.length > 0) {
        state.partnerLogoUrl = action.payload;
        state.isLoaded = true;
      }
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchBrandingUiBundle.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchBrandingUiBundle.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isLoaded = true;
      state.partnerLogoUrl = action.payload.partnerLogoUrl;
      state.epgLookBackHours = action.payload.epgLookBackHours;
      state.easDisplayMode = action.payload.easDisplayMode;
    });
    builder.addCase(fetchBrandingUiBundle.rejected, (state, action) => {
      state.isLoading = false;
      state.error =
        action.payload !== undefined && action.payload !== null
          ? action.payload
          : "Failed to fetch branding UI bundle";
    });
  }
});

export const { setPartnerLogoUrl } = brandingSlice.actions;

export const selectBrandingIsLoading = (state: UnityRootState): boolean =>
  state.branding.isLoading;

export const selectBrandingIsLoaded = (state: UnityRootState): boolean =>
  state.branding.isLoaded;

export const selectBrandingError = (state: UnityRootState): string | null =>
  state.branding.error;

export const selectPartnerLogoUrl = (state: UnityRootState): string =>
  state.branding.partnerLogoUrl;

export const selectEpgLookBackHours = (state: UnityRootState): number =>
  state.branding.epgLookBackHours;

export const selectEasDisplayMode = (state: UnityRootState): string =>
  state.branding.easDisplayMode;

export default brandingSlice.reducer;
